import { Spinner, Button as ReactButton } from '@a-ui/react';
import { FormikProps, FormikValues } from 'formik';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import Box from 'components/Box';
import CodeField from 'components/CodeField';
import { DetailLayout } from 'components/DetailLayout';
import Form from 'components/Form';
import GlobalStateMessage from 'components/GlobalStateMessage';
import LayoutTile from 'components/LayoutTile';
import Typography from 'components/Typography';
import { api } from 'hooks/api.hooks';
import { useAuthentication } from 'state/Authentication';
import { useMessage } from 'state/Message';
import { setMessage } from 'state/Message/actions';
import { translate } from 'i18n';

import './styles.scss';

const DeleteWeakProfile: React.FC<{}> = () => {
  const form = React.useRef<FormikProps<FormikValues>>(null);
  const history = useHistory();
  const {
    state: { user },
  } = useAuthentication();
  const { dispatch: dispatchMessage } = useMessage();

  const [codeType, setCodeType] = useState<'email' | 'sms'>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isProfileDeleted, setIsProfileDeleted] = useState<boolean>(false);
  const [hasConfirmed, setHasConfirmed] = useState<boolean>(false);

  const handleDelete = async (values: { code: string }) => {
    try {
      setIsLoading(true);
      const res = await api.post(`/privacy/verify${codeType}code`, {
        code: values.code,
      });

      if (res.status === 200) {
        await api.delete('me');

        if (res.status === 200) {
          setIsProfileDeleted(true);
          dispatchMessage(setMessage('privacy.weakProfile.delete.success', 'deleteWeakProfile'));
          window.location.href = '/auth/logout';
        } else {
          setIsLoading(false);
          dispatchMessage(
            setMessage('privacy.weakProfile.delete.error', 'deleteWeakProfile', 'danger'),
          );
        }
      }

      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      dispatchMessage(
        setMessage('privacy.weakProfile.delete.error', 'deleteWeakProfile', 'danger'),
      );
    }
  };

  const sendEmailCode = async (event: any) => {
    event.preventDefault();
    const email = ((<b>{user.emailPrimary}</b>) as unknown as string) || '';
    try {
      await api.post('/privacy/sendemailcode');
      dispatchMessage(
        setMessage('privacy.weakProfile.alert.email.success', 'deleteWeakProfile', 'success', {
          email,
        }),
      );
      setCodeType('email');
    } catch (e) {
      dispatchMessage(
        setMessage('privacy.weakProfile.alert.email.error', 'deleteWeakProfile', 'danger', {
          email,
        }),
      );
    }
  };

  const sendSmsCode = async (event: any) => {
    event.preventDefault();
    const phone = ((<b>{user.phonePrimary}</b>) as unknown as string) || '';
    try {
      await api.post('/privacy/sendsmscode');
      dispatchMessage(
        setMessage('privacy.weakProfile.alert.phone.success', 'deleteWeakProfile', 'success', {
          phone,
        }),
      );
      setCodeType('sms');
    } catch (e) {
      dispatchMessage(
        setMessage('privacy.weakProfile.alert.phone.error', 'deleteWeakProfile', 'danger', {
          phone,
        }),
      );
    }
  };

  const handleCancel = () => {
    history.push('/gegevens');
  };

  return (
    <DetailLayout
      backAction="/gegevens"
      backLabel="personal_data.overview.privacy.title"
      title={hasConfirmed ? 'privacy.weakProfile.title' : 'privacy.weakProfile.confirm.title'}
      description={
        hasConfirmed ? 'privacy.weakProfile.description' : 'privacy.weakProfile.confirm.description'
      }
    >
      <GlobalStateMessage location="deleteWeakProfile" />
      {!hasConfirmed ? (
        <div className="ckp-a-privacy-form__buttons">
          <div className="u-margin-top">
            <ReactButton onClick={() => setHasConfirmed(true)}>
              {translate('privacy.weakProfile.confirm')}
            </ReactButton>
          </div>
          <div className="u-margin-top">
            <ReactButton onClick={handleCancel}>{translate('common.button.cancel')}</ReactButton>
          </div>
        </div>
      ) : (
        <Box>
          <Form
            initialValues={{ code: '' }}
            onSubmit={handleDelete}
            ref={form}
            preventAutoValidation
          >
            {({ values }: FormikProps<{ code: string }>) => {
              if (isLoading) return <Spinner />;

              // stop showing content when profile is deleted, the app is redirecting to the login page
              if (isProfileDeleted) return null;
              return !codeType ? (
                <>
                  {user.emailPrimary && (
                    <LayoutTile
                      label={translate('privacy.weakProfile.confirm.submit.email')}
                      onClick={sendEmailCode}
                      toLabel={translate('privacy.weakProfile.confirm.submit.email')}
                      value={user.emailPrimary}
                    />
                  )}

                  {user.phonePrimary && (
                    <LayoutTile
                      label={translate('privacy.weakProfile.confirm.submit.phone')}
                      onClick={sendSmsCode}
                      toLabel={translate('privacy.weakProfile.confirm.submit.phone')}
                      value={user.phonePrimary}
                    />
                  )}

                  {!user.phonePrimary && !user.emailPrimary && (
                    <ul className="m-button-list u-margin-top">
                      <li>
                        <ReactButton htmlType="submit" fullWidth>
                          {translate('privacy.weakProfile.confirm.submit')}
                        </ReactButton>
                      </li>
                      <li>
                        <div className="u-margin-top">
                          <ReactButton htmlType="button" fullWidth onClick={handleCancel}>
                            {translate('common.button.cancel')}
                          </ReactButton>
                        </div>
                      </li>
                    </ul>
                  )}
                </>
              ) : (
                <>
                  <CodeField
                    id="code"
                    name="code"
                    className="u-margin-bottom-xs"
                    label={translate('accessKeys.phone.form.step2.label')}
                  />

                  <Typography classNames="u-margin-bottom-xs">
                    {translate('privacy.weakProfile.resend')}
                    <br />
                    <button
                      type="button"
                      className="a-link"
                      onClick={(event: any) =>
                        codeType === 'email' ? sendEmailCode(event) : sendSmsCode(event)
                      }
                    >
                      {translate('privacy.weakProfile.resend_link')}
                    </button>
                  </Typography>

                  <div className="ckp-a-privacy-form__buttons">
                    <div className="u-margin-top">
                      <ReactButton disabled={values.code.length !== 6} htmlType="submit">
                        {translate('privacy.weakProfile.confirm.submit')}
                      </ReactButton>
                    </div>
                    <div className="u-margin-top">
                      <ReactButton onClick={handleCancel}>
                        {translate('common.button.cancel')}
                      </ReactButton>
                    </div>
                  </div>
                </>
              );
            }}
          </Form>
        </Box>
      )}
    </DetailLayout>
  );
};

export default DeleteWeakProfile;
